import React, { useMemo } from "react";
import * as S from "./styles";

import { Button, Col, Row, Popconfirm, notification, Empty } from "antd";
import { useHistory } from "react-router";
import { Flex } from "css/styles";
import { SelectProductStep } from "./SelectProductStep";
import { SelectFacilityStep } from "./SelectFacilityStep";
import { useInventoryTransferStore } from "../../../hooks/useInventoryTransferStore";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useMutation } from "react-query";
import { createInventoryTransfer } from "../../../api/transfer";
import { useOrganizationContext } from "contextProviders/organization/OrganizationProvider";
import { Map, MapProps } from "./Map";
import { useFacilityLookup } from "hooks/api/common/useFacilityIndex";

export const InventoryTransferCreatePage: React.FC = () => {
  const { data: org } = useOrganizationContext();
  const history = useHistory();
  const { validate, reset, facility, products } = useInventoryTransferStore();

  const { mutateAsync: createTransfer, ...createMutation } = useMutation(createInventoryTransfer, {
    onSuccess: () => {
      notification.success({ message: "Inventory transfer created successfully" });
      history.push(`/organizations/${org?.id}/transfers`);
      reset();
    },
  });

  const facilityLookup = useFacilityLookup();
  const locations = useMemo(() => {
    return Object.values(facility).reduce((acc, cur) => {
      if (!cur || !facilityLookup[cur]) return acc;
      return [
        ...acc,
        {
          lat: facilityLookup[cur].location.latitude,
          lng: facilityLookup[cur].location.longitude,
          address: facilityLookup[cur].address.city,
        },
      ];
    }, [] as MapProps["markers"]);
  }, [facility, facilityLookup]);

  const handleSubmit = async () => {
    const state = validate();

    if (state === "valid") {
      return await createTransfer({
        organization_id: org?.id || "",
        from_facility: facility.origin || "",
        to_facility: facility.destination || "",
        items: products.map((p) => ({ product_id: p.id, quantity: p.quantity })),
      });
    }
  };

  const handleCancel = () => {
    history.goBack();
    reset();
  };

  const SubmitTransferButton = (
    <Button
      type="primary"
      onClick={handleSubmit}
      disabled={createMutation.isLoading}
      loading={createMutation.isLoading}
    >
      Submit Transfer
    </Button>
  );
  const CancelTransferButton = (
    <Popconfirm title="Are you sure to cancel this transfer?" onConfirm={handleCancel} okText="Yes" cancelText="No">
      <Button danger>Cancel</Button>
    </Popconfirm>
  );

  return (
    <div className="page-container" id="inventory-transfer-index-page">
      <S.PageHeader direction="vertical">
        <Flex dir="row" justify="space-between">
          <Flex gap={"1em"}>
            <S.Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()} />
            <S.PageTitle>Create Transfer</S.PageTitle>
          </Flex>
          <Flex gap={"1em"}>
            {SubmitTransferButton}
            {CancelTransferButton}
          </Flex>
        </Flex>
      </S.PageHeader>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <SelectFacilityStep />
        </Col>
        <Col span={12}>
          <S.MapContainer height="20em">
            {locations.length === 2 ? (
              <Map mapId={`${facility.origin}-${facility.destination}`} markers={locations} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </S.MapContainer>
        </Col>
        <Col span={24}>
          <SelectProductStep />
        </Col>
      </Row>
    </div>
  );
};
